import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, Loader2, Sparkles, Stethoscope, Pill, PawPrint, User as UserIcon, CheckCircle2,
} from "lucide-react";

const DEVICE_TYPES = [
  { key: "class_i",      label: "Class I (general controls)" },
  { key: "class_ii",     label: "Class II (special controls, 510(k))" },
  { key: "class_iii",    label: "Class III (PMA / life-sustaining)" },
  { key: "samd",         label: "Software as a Medical Device (SaMD)" },
  { key: "ai_ml",        label: "AI/ML enabled device" },
  { key: "ivd",          label: "In Vitro Diagnostic (IVD)" },
  { key: "combination",  label: "Combination product" },
  { key: "implantable",  label: "Implantable" },
  { key: "wearable",     label: "Wearable / Digital health" },
  { key: "imaging",      label: "Diagnostic imaging" },
  { key: "surgical",     label: "Surgical instrument" },
  { key: "reprocessing", label: "Reprocessed / reusable" },
];

const THERAPEUTIC_TYPES = [
  { key: "small_molecule", label: "Small molecule NDA (505(b)(1))" },
  { key: "generic",        label: "Generic (ANDA)" },
  { key: "biologic",       label: "Biologic (BLA)" },
  { key: "biosimilar",     label: "Biosimilar (351(k))" },
  { key: "cell_gene",      label: "Cell / gene therapy" },
  { key: "vaccine",        label: "Vaccine" },
  { key: "otc_monograph",  label: "OTC monograph" },
  { key: "505b2",          label: "505(b)(2) hybrid" },
  { key: "combination",    label: "Combination product" },
  { key: "orphan",         label: "Orphan / rare disease" },
];

const STAGES = [
  { key: "founder",       label: "Founder / pre-seed" },
  { key: "early",         label: "Early / seed" },
  { key: "series_a_plus", label: "Series A+" },
  { key: "established",   label: "Established company" },
  { key: "consulting",    label: "Consultancy / CRO" },
  { key: "academic",      label: "Academic / university" },
];

const TEAM_SIZES = ["Just me", "2-5", "6-15", "16-50", "50+"];

const ROLES = ["Regulatory Affairs", "Quality Assurance", "Founder / CEO", "R&D / Engineering", "Clinical", "Medical Affairs", "Consultant", "Other"];

export default function OnboardingWizard({ open, onDone }) {
  const { refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    domain: "",
    species: "human",
    device_types: [],
    therapeutic_types: [],
    company_stage: "",
    team_size: "",
    role: "",
    company: "",
  });

  const toggle = (key, list) => {
    setF({ ...f, [list]: f[list].includes(key) ? f[list].filter((k) => k !== key) : [...f[list], key] });
  };

  const canNext = () => {
    if (step === 1) return !!f.domain;
    if (step === 2) {
      if (f.domain === "device") return f.device_types.length > 0;
      if (f.domain === "therapeutic") return f.therapeutic_types.length > 0;
      return f.device_types.length + f.therapeutic_types.length > 0;
    }
    if (step === 3) return !!f.company_stage;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/onboarding", f);
      await refresh();
      toast.success("Welcome to Qualmitta 🎉");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save onboarding");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  const totalSteps = 3;
  const showDeviceStep = f.domain === "device" || f.domain === "both";
  const showTherapeuticStep = f.domain === "therapeutic" || f.domain === "both";

  return (
    <div data-testid="onboarding-modal" className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/85 p-4 backdrop-blur-sm">
      <div className="flex w-full max-w-3xl flex-col border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-8 py-6">
          <div className="mb-2 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#2563EB]" />
            <span className="overline text-[#2563EB]">WELCOME · STEP {step} / {totalSteps}</span>
          </div>
          <h2 className="font-serif text-3xl font-semibold leading-tight text-slate-900">
            {step === 1 && "How will you use Qualmitta?"}
            {step === 2 && "Which programs are you working on?"}
            {step === 3 && "About your team"}
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            {step === 1 && "This shapes what shows up in your workspace — pick what applies."}
            {step === 2 && "Multi-select is fine. We'll pin the right templates and dashboards."}
            {step === 3 && "Optional but helps us tailor onboarding calls, pricing, and playbooks."}
          </p>
        </div>

        <div className="min-h-[300px] flex-1 p-8">
          {step === 1 && (
            <div className="grid gap-3 md:grid-cols-3">
              {[
                { key: "device",      icon: Stethoscope, title: "Medical Devices", desc: "510(k), PMA, De Novo, IVD, SaMD, EU MDR" },
                { key: "therapeutic", icon: Pill,        title: "Therapeutics",    desc: "IND, NDA, ANDA, BLA, CMC, Orphan / BT" },
                { key: "both",        icon: Sparkles,    title: "Both",            desc: "Combination products, drug-device combos" },
              ].map((d) => {
                const I = d.icon;
                const active = f.domain === d.key;
                return (
                  <button
                    key={d.key}
                    data-testid={`onboarding-domain-${d.key}`}
                    onClick={() => setF({ ...f, domain: d.key })}
                    className={`flex flex-col items-start gap-2 border p-5 text-left transition-all ${
                      active ? "border-[#2563EB] bg-[#2563EB]/5 ring-1 ring-[#2563EB]" : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <I className={`h-6 w-6 ${active ? "text-[#2563EB]" : "text-slate-500"}`} strokeWidth={1.5} />
                    <div className="font-serif text-lg font-semibold text-slate-900">{d.title}</div>
                    <div className="text-xs text-slate-500">{d.desc}</div>
                    {active && <CheckCircle2 className="ml-auto mt-2 h-4 w-4 text-[#2563EB]" />}
                  </button>
                );
              })}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {showDeviceStep && (
                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Stethoscope className="h-4 w-4 text-[#2563EB]" />
                      <span className="overline text-[#2563EB]">MEDICAL DEVICES</span>
                    </div>
                    <div className="flex overflow-hidden border border-slate-200 text-[11px]">
                      {[
                        { k: "human",      i: UserIcon,   l: "Human" },
                        { k: "veterinary", i: PawPrint,   l: "Veterinary" },
                        { k: "both",       i: Sparkles,   l: "Both" },
                      ].map((s) => {
                        const I = s.i;
                        return (
                          <button
                            key={s.k}
                            data-testid={`onboarding-species-${s.k}`}
                            onClick={() => setF({ ...f, species: s.k })}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${
                              f.species === s.k ? "bg-[#2563EB] text-white" : "text-slate-600 hover:bg-slate-50"
                            }`}
                          >
                            <I className="h-3 w-3" /> {s.l}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {DEVICE_TYPES.map((t) => (
                      <label key={t.key} className={`flex cursor-pointer items-start gap-2 border p-2.5 text-xs transition-all ${
                        f.device_types.includes(t.key) ? "border-[#2563EB] bg-[#2563EB]/5" : "border-slate-200 bg-white hover:border-slate-300"
                      }`}>
                        <input
                          type="checkbox"
                          checked={f.device_types.includes(t.key)}
                          onChange={() => toggle(t.key, "device_types")}
                          data-testid={`onboarding-devtype-${t.key}`}
                          className="mt-0.5 accent-[#2563EB]"
                        />
                        <span className="text-slate-700">{t.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {showTherapeuticStep && (
                <div>
                  <div className="mb-3 flex items-center gap-2">
                    <Pill className="h-4 w-4 text-[#2563EB]" />
                    <span className="overline text-[#2563EB]">THERAPEUTICS</span>
                  </div>
                  <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                    {THERAPEUTIC_TYPES.map((t) => (
                      <label key={t.key} className={`flex cursor-pointer items-start gap-2 border p-2.5 text-xs transition-all ${
                        f.therapeutic_types.includes(t.key) ? "border-[#2563EB] bg-[#2563EB]/5" : "border-slate-200 bg-white hover:border-slate-300"
                      }`}>
                        <input
                          type="checkbox"
                          checked={f.therapeutic_types.includes(t.key)}
                          onChange={() => toggle(t.key, "therapeutic_types")}
                          data-testid={`onboarding-thertype-${t.key}`}
                          className="mt-0.5 accent-[#2563EB]"
                        />
                        <span className="text-slate-700">{t.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <div className="overline mb-2 text-slate-500">Company stage</div>
                <div className="grid gap-2 md:grid-cols-3">
                  {STAGES.map((s) => (
                    <button
                      key={s.key}
                      data-testid={`onboarding-stage-${s.key}`}
                      onClick={() => setF({ ...f, company_stage: s.key })}
                      className={`border p-2.5 text-left text-xs transition-all ${
                        f.company_stage === s.key ? "border-[#2563EB] bg-[#2563EB]/5" : "border-slate-200 bg-white hover:border-slate-300"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <label>
                  <div className="overline mb-1 text-slate-500">Team size</div>
                  <select data-testid="onboarding-team-size" value={f.team_size} onChange={(e) => setF({ ...f, team_size: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900">
                    <option value="">Select…</option>
                    {TEAM_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
                <label>
                  <div className="overline mb-1 text-slate-500">Your role</div>
                  <select data-testid="onboarding-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900">
                    <option value="">Select…</option>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label>
                  <div className="overline mb-1 text-slate-500">Company (optional)</div>
                  <input data-testid="onboarding-company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-8 py-4">
          {step > 1 ? (
            <button data-testid="onboarding-back" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : <span />}
          {step < totalSteps ? (
            <button
              data-testid="onboarding-next"
              onClick={() => setStep(step + 1)}
              disabled={!canNext()}
              className="inline-flex items-center gap-2 bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-40"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              data-testid="onboarding-finish"
              onClick={submit}
              disabled={!canNext() || saving}
              className="inline-flex items-center gap-2 bg-[#2563EB] px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-40"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Finish and enter workspace
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
